import { useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import FrontLayout from '../../components/layouts/FrontLayout';
import Button from '../../components/ui/Button';
import TitledPanel from 'src/components/ui/TitledPanel';

export default function EmailVerification() {
  const { resendVerificationEmail, logout } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0); // No cooldown on initial page load

  // Countdown timer effect
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setTimeout(() => {
      setCooldownSeconds(cooldownSeconds - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const handleResendEmail = async () => {
    setIsResending(true);
    setError('');

    try {
      await resendVerificationEmail();
      setCooldownSeconds(60); // Start 60 second cooldown
    } catch (err: any) {
      setError(err?.message || 'Failed to send verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const isButtonDisabled = isResending || cooldownSeconds > 0;

  return (
    <FrontLayout showTrustedBy={false}>
      <TitledPanel title="Verify your email address">
        <p className="text-sm text-content-subtle">
          Please check your email, including your spam folder, for a verification link to continue. If you don't see the
          email, please request a new verification email below.
        </p>
        {error && (
          <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            onClick={handleResendEmail}
            disabled={isButtonDisabled}
            variant="primary"
            fullWidth
            iconPosition="left"
            loading={isResending}
          >
            {isResending
              ? 'Sending...'
              : cooldownSeconds > 0
                ? `Wait ${cooldownSeconds}s to resend`
                : 'Resend verification email'}
          </Button>

          <Button onClick={handleLogout} variant="outline" fullWidth>
            Log out
          </Button>
        </div>

        <p className="text-sm text-content-subtle">
          Once you've verified your email, refresh this page or log in again to continue.
        </p>
      </TitledPanel>
    </FrontLayout>
  );
}
